import { useState } from 'react'
import { Link } from 'react-router'
import { useT } from '../i18n/useT'

const tierMeta = [
  { minimum: 25, featured: false },
  { minimum: 75, featured: true },
]

const freqRuns = [12, 24, 26, 52]

const remittanceTypes = ['CRA', 'EHT', 'RQ']

const remitFreqRuns = [4, 12, 24, 48] as const // quarterly, monthly, threshold 1, threshold 2

const RATE = 2 // $ per employee per pay run

export default function Pricing() {
  const t = useT()
  const [tierIdx, setTierIdx] = useState(0)
  const [freqIdx, setFreqIdx] = useState(2)
  const [employees, setEmployees] = useState(10)
  const [remitFreqs, setRemitFreqs] = useState<Record<string, number>>({ CRA: 1 })

  const tier = { ...tierMeta[tierIdx], ...t.pricing.tiers[tierIdx] }
  const runs = freqRuns[freqIdx]

  const perEmployee = employees * RATE
  const base = Math.max(tier.minimum, perEmployee)
  const minApplies = perEmployee < tier.minimum
  const remitTypes = remittanceTypes.filter((r) => r in remitFreqs)
  const remitAnnual = remitTypes.reduce(
    (sum, r) => sum + 5 * remitFreqRuns[remitFreqs[r]],
    0
  )
  const perRun = base
  const annual = base * runs + remitAnnual
  const remitLabel = (r: string) => t.pricing.est.remitTypes[remittanceTypes.indexOf(r)] ?? r

  function toggleRemittance(r: string) {
    setRemitFreqs((cur) => {
      const next = { ...cur }
      if (r in next) delete next[r]
      else next[r] = 1 // default: monthly
      return next
    })
  }

  function setRemitFreq(r: string, fi: number) {
    setRemitFreqs((cur) => ({ ...cur, [r]: fi }))
  }

  return (
    <>
      {/* hero */}
      <section className="bg-gradient-to-b from-brand-50 via-white to-white">
        <div className="mx-auto max-w-7xl px-8 pt-20 pb-16 text-center">
          <h1 className="text-5xl font-extrabold mb-5">{t.pricing.h}</h1>
          <p className="text-lg text-ink-mute max-w-5xl mx-auto">{t.pricing.sub}</p>
        </div>
      </section>

      {/* tiers */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-8 pb-24 grid grid-cols-2 gap-8">
          {t.pricing.tiers.map((tierCard, i) => (
            <div
              key={tierCard.name}
              className={`rounded-2xl p-10 flex flex-col ${
                tierMeta[i].featured
                  ? 'border-2 border-brand-600 shadow-[0_24px_60px_-24px_rgb(22_163_74/0.3)] relative'
                  : 'border border-slate-200'
              }`}
            >
              {tierCard.badge && (
                <span className="absolute -top-3.5 left-10 rounded-full bg-brand-600 text-white text-xs font-bold px-3.5 py-1.5">
                  {tierCard.badge}
                </span>
              )}
              <h2 className="text-2xl font-extrabold mb-2">{tierCard.name}</h2>
              <p className="text-[15px] text-ink-mute mb-7">{tierCard.blurb}</p>
              <p className="mb-1.5">
                <span className="font-display text-5xl font-extrabold text-ink">$2</span>
                <span className="text-ink-mute font-medium">{t.pricing.perEmployee}</span>
              </p>
              <p className="text-sm font-semibold text-brand-700 mb-8">
                {t.pricing.minLabel(tierMeta[i].minimum)}
              </p>
              <ul className="space-y-3">
                {tierCard.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-[15px]">
                    <svg viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.4} className="w-5 h-5 shrink-0 mt-0.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* estimator + extras */}
      <section className="bg-mist">
        <div className="mx-auto max-w-7xl px-8 py-24">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-4xl font-extrabold mb-4">{t.pricing.est.h}</h2>
            <p className="text-lg text-ink-mute">{t.pricing.est.sub}</p>
          </div>
          <div className="grid grid-cols-12 gap-10 items-start">
            {/* estimator */}
            <div className="col-span-7 rounded-2xl border border-slate-100 bg-white shadow-[0_24px_60px_-24px_rgb(15_23_42/0.18)] p-10">
              {/* tier toggle */}
              <p className="text-sm font-semibold text-ink-soft mb-3">{t.pricing.est.plan}</p>
              <div className="grid grid-cols-2 gap-2.5 mb-9">
                {t.pricing.tiers.map((tierBtn, i) => (
                  <button
                    key={tierBtn.name}
                    onClick={() => setTierIdx(i)}
                    className={`rounded-md border px-4 py-3 text-sm font-semibold transition-colors ${
                      tierIdx === i
                        ? 'border-brand-600 bg-brand-50 text-brand-800'
                        : 'border-slate-200 text-ink-soft hover:border-slate-300'
                    }`}
                  >
                    {tierBtn.name}
                    <span className="block text-xs font-medium text-ink-mute mt-0.5">
                      {t.pricing.est.minRun(tierMeta[i].minimum)}
                    </span>
                  </button>
                ))}
              </div>

              {/* frequency */}
              <p className="text-sm font-semibold text-ink-soft mb-3">{t.pricing.est.freq}</p>
              <div className="grid grid-cols-4 gap-2.5 mb-9">
                {t.pricing.est.frequencies.map((label, i) => (
                  <button
                    key={label}
                    onClick={() => setFreqIdx(i)}
                    className={`rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors ${
                      freqIdx === i
                        ? 'border-brand-600 bg-brand-50 text-brand-800'
                        : 'border-slate-200 text-ink-soft hover:border-slate-300'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* employees */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-ink-soft">{t.pricing.est.employees}</p>
                <p className="font-display text-2xl font-extrabold text-ink">{employees}</p>
              </div>
              <input
                type="range"
                min={1}
                max={200}
                value={employees}
                onChange={(e) => setEmployees(Number(e.target.value))}
                className="w-full accent-brand-600 mb-9"
              />

              {/* remittances */}
              <p className="text-sm font-semibold text-ink-soft mb-1.5">
                {t.pricing.est.remit}{' '}
                <span className="font-normal text-ink-mute">{t.pricing.est.remitNote}</span>
              </p>
              <div className="flex gap-2.5 mb-4">
                {remittanceTypes.map((r) => (
                  <button
                    key={r}
                    onClick={() => toggleRemittance(r)}
                    className={`rounded-md border px-5 py-2.5 text-sm font-semibold transition-colors ${
                      r in remitFreqs
                        ? 'border-brand-600 bg-brand-50 text-brand-800'
                        : 'border-slate-200 text-ink-soft hover:border-slate-300'
                    }`}
                  >
                    {remitLabel(r)}
                  </button>
                ))}
              </div>

              {/* remittance frequency — per type, independent of payroll frequency */}
              {remitTypes.length > 0 ? (
                <div className="space-y-2.5 mb-10">
                  <p className="text-sm font-semibold text-ink-soft">
                    {t.pricing.est.remitFreq}
                  </p>
                  {remitTypes.map((r) => (
                    <div key={r} className="grid grid-cols-[3.5rem_1fr] items-center gap-3">
                      <span className="text-sm font-semibold text-ink-mute">{remitLabel(r)}</span>
                      <div className="grid grid-cols-4 gap-2.5">
                        {t.pricing.est.remitFrequencies.map((label, i) => (
                          <button
                            key={label}
                            onClick={() => setRemitFreq(r, i)}
                            className={`rounded-md border px-3 py-2 text-[13px] font-semibold transition-colors ${
                              remitFreqs[r] === i
                                ? 'border-brand-600 bg-brand-50 text-brand-800'
                                : 'border-slate-200 text-ink-soft hover:border-slate-300'
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mb-6" />
              )}

              {/* result */}
              <div className="rounded-2xl bg-mist p-7 grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-ink-mute mb-1.5">{t.pricing.est.perRun}</p>
                  <p className="font-display text-4xl font-extrabold text-ink">
                    ${perRun.toLocaleString()}
                  </p>
                  <p className="text-xs text-ink-mute mt-2 leading-relaxed">
                    {minApplies
                      ? t.pricing.est.minApplies(employees, perEmployee, tier.minimum, tier.name)
                      : t.pricing.est.normal(employees, perEmployee)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-ink-mute mb-1.5">
                    {t.pricing.est.perYear(t.pricing.est.frequencies[freqIdx], runs)}
                  </p>
                  <p className="font-display text-4xl font-extrabold text-brand-600">
                    ${annual.toLocaleString()}
                  </p>
                  {remitTypes.length > 0 && (
                    <p className="text-xs text-ink-mute mt-2 leading-relaxed">
                      {t.pricing.est.annualRemit(remitTypes.length, remitAnnual)}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-4 leading-relaxed">
                {t.pricing.est.foot}
              </p>
            </div>

            {/* right column */}
            <div className="col-span-5 space-y-6">
              <div className="rounded-2xl border border-slate-100 bg-white p-8">
                <h3 className="text-lg font-bold mb-5">{t.pricing.optional.h}</h3>
                <div className="space-y-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[15px] font-medium text-ink-soft">{t.pricing.optional.remitName}</p>
                      <p className="text-xs text-ink-mute mt-0.5">{t.pricing.optional.remitNote}</p>
                    </div>
                    <p className="text-right shrink-0">
                      <span className="font-display text-xl font-extrabold text-brand-600">$5</span>
                      <span className="block text-xs text-ink-mute">{t.pricing.optional.remitPer}</span>
                    </p>
                  </div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[15px] font-medium text-ink-soft">{t.pricing.optional.roeName}</p>
                      <p className="text-xs text-ink-mute mt-0.5">{t.pricing.optional.roeNote}</p>
                    </div>
                    <p className="text-right shrink-0">
                      <span className="font-display text-xl font-extrabold text-brand-600">$3</span>
                      <span className="block text-xs text-ink-mute">{t.pricing.optional.roePer}</span>
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-ink text-white p-8">
                <h3 className="text-lg font-bold mb-5 text-white">{t.pricing.includedH}</h3>
                <ul className="space-y-3">
                  {t.pricing.included.map((f) => (
                    <li key={f} className="flex items-center gap-3 text-[15px]">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#5fcf80" strokeWidth={2.4} className="w-5 h-5 shrink-0">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-2xl border border-brand-100 bg-brand-50 p-6">
                <p className="text-sm font-semibold text-brand-900 mb-1">{t.pricing.noFees.h}</p>
                <p className="text-sm text-brand-800/80 leading-relaxed">
                  {t.pricing.noFees.body}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-brand-700 to-brand-900 text-white">
        <div className="mx-auto max-w-7xl px-8 py-16 text-center">
          <h2 className="text-3xl font-extrabold text-white mb-4">{t.pricing.cta.h}</h2>
          <p className="text-brand-100 max-w-none mx-auto mb-8 whitespace-nowrap">{t.pricing.cta.sub}</p>
          <div className="flex items-center justify-center gap-4">
            <Link
              to="/"
              className="rounded-md bg-white text-brand-800 font-semibold px-7 py-3.5 hover:bg-brand-50 transition-colors"
            >
              {t.pricing.cta.start}
            </Link>
            <Link
              to="/book-a-demo"
              className="rounded-md border border-white/30 font-semibold px-7 py-3.5 hover:bg-white/10 transition-colors"
            >
              {t.pricing.cta.demo}
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
